import {
  CounterResponse,
  CounterType,
  RoutineQuestionConfig,
  RoutineResponseState,
} from '@gripp/shared-logic';
import { t } from 'i18next';
import { FC } from 'react';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import { routineMessageStyles } from '../../../components/routineMessageStyles';
import { RoutineQuestionMessage, RoutineQuestionMessageProps } from '../types';

export const ExistingRoutineQuestionMessage: RoutineQuestionMessage<
  RoutineQuestionConfig,
  RoutineResponseState
> = (
  props: RoutineQuestionMessageProps<
    RoutineQuestionConfig,
    RoutineResponseState
  >
) => {
  const genericResponse = props.response as any;

  if (
    genericResponse?.counterType ||
    genericResponse?.uom ||
    genericResponse?.userCount !== undefined ||
    genericResponse?.count !== undefined
  ) {
    const RoutineTextTypeC =
      RoutineTextType[
        (genericResponse?.counterType as CounterType) ?? CounterType.Cumulative
      ];
    return <RoutineTextTypeC {...genericResponse} />;
  }
  const routineOpenEndedResponseText = genericResponse?.openEndedResponse
    ? `${t('routines.status.admin.openEndedResponse')}: ${
        genericResponse.openEndedResponse
      }`
    : '';
  const routineText = `Status: ${genericResponse.status}
${routineOpenEndedResponseText}`;

  return (
    <Text style={props.textStyle}>{genericResponse?.count ?? routineText}</Text>
  );
};

const RoutineQuestionCounter = (props: CounterResponse) => {
  const uom = props?.uom ?? '';
  const countDefined =
    props.count !== undefined &&
    props.count !== null &&
    !Number.isNaN(props.count);
  const countDisplay = countDefined ? props.count : '--';
  const userDefined =
    props.userCount !== undefined &&
    props.userCount !== null &&
    !Number.isNaN(props.userCount) &&
    props.userCount !== 0;
  const oldDefined =
    props.oldValue !== undefined &&
    props.oldValue !== null &&
    !Number.isNaN(props.oldValue);
  return (
    <View>
      {oldDefined && (
        <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
          {t('routines.message.originalValue')}: {props.oldValue} {uom}
        </Text>
      )}
      {userDefined && (
        <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
          {t(
            `routines.message.${
              (props.oldValue ?? 0) > (props.count as number)
                ? 'removed'
                : 'added'
            }`
          )}
          : {Math.abs(props.userCount as number)} {uom}
        </Text>
      )}
      <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
        {t('routines.message.newValue')}: {countDisplay}{' '}
        {countDisplay === '--' ? '' : uom}
      </Text>
    </View>
  );
};

const RoutineSimpleText = (props: CounterResponse) => {
  const uom = props?.uom?.toString() ?? '';
  const countDefined =
    props.count !== undefined &&
    props.count !== null &&
    !Number.isNaN(props.count);
  const count = countDefined ? props.count.toString() : '--';

  return (
    <View>
      <Text style={[routineMessageStyles.text, { display: 'flex' }]}>
        {count} {count === '--' ? '' : uom}
      </Text>
    </View>
  );
};

const CumulativeCounter = (props: CounterResponse) => (
  <RoutineQuestionCounter {...props} />
);
const SimpleCounter = (props: CounterResponse) => (
  <RoutineSimpleText {...props} />
);
const TallyCounter = (props: CounterResponse) => (
  <RoutineQuestionCounter {...props} />
);

type RoutineTextType = FC<CounterResponse>;
const RoutineTextType: Record<CounterType, RoutineTextType> = {
  [CounterType.Cumulative]: CumulativeCounter,
  [CounterType.Simple]: SimpleCounter,
  [CounterType.Tally]: TallyCounter,
};
